/**
 * Copyright (c) 2026, CYBRA Corp. All rights reserved.
 */
import { Injectable } from "@angular/core";
import { RestService } from "./rest-service";
import { Url_Base } from "app/rest-constants";

/** UUID of the PVH's "Scan Chamber" Service Type created in Edgefinity.  */
export const Pvh_ScanChamberServiceType_Uuid = "82c4cf48-8720-4fd9-b7dd-fba71d2b0c78";

/**
 * Service for PVH 'Scan Chamber' process customizations.
 */
@Injectable()
export class PvhScanChamberService {

  /** Constructor */
  constructor(
    private restService: RestService
  ) { }


  /**
   * Calls the "Get State Info" API for the Scan Chamber with the given ID.
   * Returns the current state information for the Scan Chamber.
   */
  public getState(serviceId: number | string): Promise<PvhScanChamberStateInfoDto> {
    const url = `${Url_Base}/pvh/scan-chamber/${serviceId}/get-state-info`;
    return this.restService.get<PvhScanChamberStateInfoDto>(url);
  }
  
  /**
   * Calls the "Get State Info" API for the Scan Chamber with the given ID.
   * Returns the current state information for the Scan Chamber.
   */
  public enterCartonId(serviceId: number | string, cartonId: string): Promise<PvhScanChamberStateInfoDto> {
    const url = `${Url_Base}/pvh/scan-chamber/${serviceId}/enter-carton-id?carton_id=${cartonId}`;
    return this.restService.post<PvhScanChamberStateInfoDto>(url, null);
  }
  
  /**
   * Calls the "Clear Carton" API for the Scan Chamber with the given ID.
   * Returns the current state information for the Scan Chamber.
   */
  public clearCarton(serviceId: number | string): Promise<PvhScanChamberStateInfoDto> {
    const url = `${Url_Base}/pvh/scan-chamber/${serviceId}/clear-carton`;
    return this.restService.post<PvhScanChamberStateInfoDto>(url, null);
  }
  
  /**
   * Calls the "Clear Carton" API for the Scan Chamber with the given ID.
   * Returns the current state information for the Scan Chamber.
   */
  public processCarton(serviceId: number | string, lineItemOverrides?: PvhLineItemOverride[]): Promise<PvhScanChamberStateInfoDto> {
    const uiInfo = {
      line_item_overrides: lineItemOverrides
    } as PvhProcessCartonUiInfo;
    
    const url = `${Url_Base}/pvh/scan-chamber/${serviceId}/process-carton`;
    return this.restService.post<PvhScanChamberStateInfoDto>(url, uiInfo);
  }

  /**
   * Calls the backend to retrieve the available Reason Code options that can be applied
   * to an attempt being processed by the Scan Chamber.
   */
  public getReasonCodeOptions(): Promise<PvhReasonCodeOption[]> {
    const url = `${Url_Base}/pvh/scan-chamber/reason-codes`;
    return this.restService.get<PvhReasonCodeOption[]>(url);
  }

}

export interface PvhProcessCartonUiInfo {
  line_item_overrides?: PvhLineItemOverride[];
}

export interface PvhLineItemOverride {
  upc: string;
  audit_quantity?: number;
  reason_codes?: PvhReasonCodeAndComment[];
}

/**
 * Reason Code Option for the Scan Chamber process
 */
export interface PvhReasonCodeOption {
  reason_code: string;
  description: string;
  allows_comments: boolean;
}

/**
 * Contains User selected Reason Code and user entered comments
 */
export interface PvhReasonCodeAndComment {
  reasonCode?: PvhReasonCodeOption;
  comment?: string;
}

/**
 * Contains the User entered quantity and Reason Codes to 
 * override / apply to a Line Item
 */
export interface PvhAuditQuantityAndReasonCodes {
  lineItem: PvhScanChamberAttemptLineDto;
  auditQuantity?: number;
  reasonCodesAndComments?: PvhReasonCodeAndComment[];
}

/**
 * DTO used to return the current state / information about PVH's 
 * Scan Chamber process being performed. 
 */
export interface PvhScanChamberStateInfoDto {
  state: PvhScanChamberStateEnum;
  attempt?: PvhScanChamberAttemptDto;
}

/**
 * DTO used to represent a validation / audit attempt being performed
 * by PVH's Scan Chamber process.
 */
export interface PvhScanChamberAttemptDto {
  scanned_barcode: string;
  case_number?: string;
  carton_number?: string;
  use_sku_quantity?: boolean;
  tunnel_reader_name?: string;
  tunnel_read_timestamp?: string;
  line_items: PvhScanChamberAttemptLineDto[];
}

/**
 * DTO used to represent a Line Item for a attempt being performed by
 * PVH's Scan Chamber process
 */
export interface PvhScanChamberAttemptLineDto {
  upc: string;
  sku?: string;
  style?: string;
  size?: string;
  color?: string;
  sku_quantity?: number;
  rfid_expected_quantity?: number;
  tunnel_quantity?: number;
  required_quantity: number;
  scanned_quantity: number;
}

/**
 * Possible States for PVH's Scan Chamber process
 */
export enum PvhScanChamberStateEnum {
  
  /**
   * Waiting on the Carton ID to be scanned so that the 
   * contents can be looked up
   */
  WaitingOnCartonScan = "WaitingOnCartonScan",

  /**
   * Carton Contents are being retrieved...
   */
  RetrievingCartonContents = "RetrievingCartonContents",

  /**
   * Carton Contents retrieved / displayed and reads are being
   * applied to it.  (Carton is being validated)
   */
  CartonBeingValidated = "CartonBeingValidated",

  /**
   * Carton Contents retrieved and reads might habe been applied
   * to it.  It is currently being sent to their side / saved on 
   * our side, any new reads are being ignored. 
   */
  CartonBeingProcessed = "CartonBeingProcessed"

}


